import React, {useEffect, useMemo, useRef, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import {CommonLoading} from '../../../../Utils/commonLoading'
import toaster from '../../../../Utils/toaster'
import {LogsViewTable} from '../../../pages/dashboard/Logs/LogsViewTable'
import InterpreterAiQcFilterDropdown from './InterpreterAiQcFilterDropdown'
import InterpreterAiQcTable, {type InterpreterAiQcTableRow} from './InterpreterAiQcTable'
import {
  buildInterpreterAiQcFilterPayload,
  defaultInterpreterAiQcFilter,
  getInterpreterAiQcLogs,
  normalizeAiQcRow,
  normalizeAiQcSummary,
  paginationFromResponse,
  type InterpreterAiQcFilterValues,
  type InterpreterAiQcLogRow,
  type InterpreterAiQcSortField,
} from './interpreterAiQcApi'

const SCORE_COLOR = '#8C913B'

const itemsPerPage = Number(process.env.REACT_APP_PAGINATION_ITEMS_PER_PAGE) || 10

type Props = {
  linguistId: string
}

type NormalizedRow = ReturnType<typeof normalizeAiQcRow>

const CSV_HEADERS = [
  'Call ID',
  'Client Name',
  'Requester Name',
  'AI QC Status',
  'Name',
  'Language',
  'ID',
  'Call Date Time',
]

function escapeCsv(value: unknown): string {
  const str = value === null || value === undefined ? '' : String(value)
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`
  return str
}

function formatCallDateTime(iso: string): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  } catch {
    return iso
  }
}

function rowsToCsv(rows: NormalizedRow[]): string {
  const lines = rows.map((r) =>
    [
      escapeCsv(r.callId),
      escapeCsv(r.customerName),
      escapeCsv(r.requesterName),
      escapeCsv(r.aiQcStatusPass ? 'Pass' : 'Fail'),
      escapeCsv(r.nameMentioned ? 'Mentioned' : 'Missed'),
      escapeCsv(r.languageMentioned ? 'Mentioned' : 'Missed'),
      escapeCsv(r.idMentioned ? 'Mentioned' : 'Missed'),
      escapeCsv(formatCallDateTime(r.callDateTime)),
    ].join(',')
  )
  return [CSV_HEADERS.join(','), ...lines].join('\n')
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob(['\uFEFF' + csv], {type: 'text/csv;charset=utf-8;'})
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const InterpreterAiQcView: React.FC<Props> = ({linguistId}) => {
  const [rows, setRows] = useState<NormalizedRow[]>([])
  const [summary, setSummary] = useState({
    overallComplianceScore: 0,
    totalCallsAudited: 0,
    passRate: 0,
  })
  const [filterData, setFilterData] = useState<InterpreterAiQcFilterValues>(
    defaultInterpreterAiQcFilter()
  )
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage)
  const [totalRecords, setTotalRecords] = useState(0)
  const [sortBy, setSortBy] = useState<InterpreterAiQcSortField>('calldatetime')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [externalCallOpen, setExternalCallOpen] = useState<{
    callId: string
    initialContactId?: string | null
    rowItem?: any
  } | null>(null)
  const latestRequestIdRef = useRef(0)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const logsTableRef = useRef(null)

  const filterPayload = useMemo(
    () => buildInterpreterAiQcFilterPayload(filterData),
    [filterData]
  )

  const fetchRows = async (page: number, search = appliedSearch) => {
    if (!linguistId) return
    const requestId = ++latestRequestIdRef.current
    setIsLoading(true)
    try {
      const resp = await getInterpreterAiQcLogs(
        linguistId,
        filterPayload,
        page,
        rowsPerPage,
        search,
        sortBy,
        sortOrder
      )
      if (requestId !== latestRequestIdRef.current) return

      const rawRows: InterpreterAiQcLogRow[] = resp.data ?? resp.Data ?? []
      setRows(rawRows.map(normalizeAiQcRow))
      setSummary(normalizeAiQcSummary(resp.summary ?? resp.Summary))

      const pag = paginationFromResponse(resp)
      setTotalPages(Math.max(1, pag.lastPage))
      setTotalRecords(pag.total)
      setCurrentPage(page)
    } catch (err) {
      if (requestId !== latestRequestIdRef.current) return
      console.error('fetchInterpreterAiQc error', err)
      toaster('error', 'Failed to load AI QC results')
    } finally {
      if (requestId === latestRequestIdRef.current) setIsLoading(false)
    }
  }

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      setAppliedSearch(searchInput.trim())
      setCurrentPage(1)
    }, 300)
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [searchInput])

  useEffect(() => {
    if (linguistId) fetchRows(currentPage, appliedSearch)
  }, [linguistId, filterPayload, appliedSearch, currentPage, rowsPerPage, sortBy, sortOrder])

  const handleSort = (field: InterpreterAiQcSortField) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortOrder(field === 'calldatetime' ? 'desc' : 'asc')
    }
    setCurrentPage(1)
  }

  const handleCallIdClick = (row: InterpreterAiQcTableRow) => {
    setExternalCallOpen({
      callId: row.callId,
      initialContactId: row.contactId || null,
      rowItem: {
        code: row.callId,
        id: row.callHeaderId,
        initialContactId: row.contactId,
        customer: row.customerName,
      },
    })
  }

  const handleExport = async () => {
    if (!linguistId) return
    setIsExporting(true)
    try {
      const maxItemsPerPage = 500
      let page = 1
      let all: NormalizedRow[] = []
      let lastPage = 1

      while (page <= lastPage && page <= 200) {
        const resp = await getInterpreterAiQcLogs(
          linguistId,
          filterPayload,
          page,
          maxItemsPerPage,
          appliedSearch,
          sortBy,
          sortOrder
        )
        const chunk = (resp.data ?? resp.Data ?? []).map(normalizeAiQcRow)
        all = [...all, ...chunk]
        lastPage = paginationFromResponse(resp).lastPage || 1
        if (chunk.length === 0 || page >= lastPage) break
        page += 1
      }

      if (all.length === 0) {
        toaster('warning', 'No data to export')
        return
      }

      downloadCsv(
        rowsToCsv(all),
        `interpreter-ai-qc-${linguistId}-${new Date().toISOString().slice(0, 10)}.csv`
      )
    } catch (err) {
      console.error('exportInterpreterAiQc error', err)
      toaster('error', 'Failed to export AI QC data')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      {(isLoading || isExporting) && <CommonLoading />}

      <div className='card border mt-5 mb-5' style={{borderRadius: 12, backgroundColor: '#F6F7F9'}}>
        <div className='card-body p-6'>
          <div className='d-flex align-items-center mb-6'>
            <i
              className='bi bi-shield-check me-2'
              style={{color: '#DC3545', fontSize: '1.1rem'}}
            ></i>
            <span className='fs-7 fw-semibold text-uppercase letter-spacing-1'>
              AI Quality Assurance Agent
            </span>
          </div>

          <div className='d-flex justify-content-between align-items-center flex-wrap gap-4'>
            <div>
              <div className='fw-bold fs-3 text-dark mb-2'>Overall Compliance Score</div>
              <div className='fw-bold' style={{fontSize: '4rem', lineHeight: 1, color: SCORE_COLOR}}>
                {summary.overallComplianceScore.toFixed(1)}%
              </div>
            </div>

            <div className='d-flex flex-column gap-4 pe-2'>
              <div>
                <div className='text-muted fs-4 fw-semibold text-uppercase mb-1'>
                  Total Calls Audited
                </div>
                <div className='fw-bold fs-1' style={{color: SCORE_COLOR}}>
                  {summary.totalCallsAudited.toLocaleString()}
                </div>
              </div>
              <div>
                <div className='text-muted fs-4 fw-semibold text-uppercase mb-1'>Pass Rate</div>
                <div className='fw-bold fs-1' style={{color: SCORE_COLOR}}>
                  {summary.passRate.toFixed(1)}%
                </div>
              </div>
            </div>
            <div></div>
          </div>
        </div>
      </div>

      <div className='card-body p-0 mt-3' style={{minHeight: '70vh'}}>
        <div className='d-flex justify-content-between mb-3 align-items-center flex-nowrap gap-3'>
          <div className='card-title d-flex align-items-center me-4 mt-2 flex-shrink-0'>
            <h5 className='text-black fs-4 fw-bold mb-0'>AI QC Logs</h5>
          </div>

          <div className='d-flex justify-content-end align-items-center flex-nowrap gap-2 ms-auto'>
            <input
              type='text'
              className='form-control form-control-sm'
              style={{width: 220, minWidth: 160, maxWidth: 280}}
              placeholder='Search by ID or Client...'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <InterpreterAiQcFilterDropdown
              initialValues={filterData}
              onApply={(vals) => {
                setFilterData(vals)
                setCurrentPage(1)
              }}
              onReset={() => {
                setFilterData(defaultInterpreterAiQcFilter())
                setCurrentPage(1)
              }}
            />
            <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip-ai-qc-export'>Export</Tooltip>}>
              <button
                type='button'
                className='btn btn-sm btn-primary flex-shrink-0'
                onClick={handleExport}
                disabled={isExporting}
              >
                <KTSVG path='/media/icons/duotune/files/fil021.svg' className='svg-icon-3 me-1' />
                Export
              </button>
            </OverlayTrigger>
          </div>
        </div>

        <div className='row g-1'>
          <div
            className='py-0 pt-3 pb-3 px-3'
            style={{borderRadius: 12, backgroundColor: '#F6F7F9'}}
          >
            <InterpreterAiQcTable
              rows={rows}
              currentPage={currentPage}
              totalPages={totalPages}
              rowsPerPage={rowsPerPage}
              totalItems={totalRecords}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onPageChange={setCurrentPage}
              onRowsPerPageChange={(newRowsPerPage) => {
                setRowsPerPage(newRowsPerPage)
                setCurrentPage(1)
              }}
              onCallIdClick={handleCallIdClick}
              formatCallDateTime={formatCallDateTime}
            />
          </div>
        </div>
      </div>

      <LogsViewTable
        className=''
        isLoading={false}
        call_log={[]}
        tableRef={logsTableRef}
        title='OPI Logs'
        hideTable
        externalCallOpen={externalCallOpen}
        onExternalCallOpened={() => setExternalCallOpen(null)}
      />
    </>
  )
}

export default InterpreterAiQcView
